import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Shared moderation UI for the web client. Mirrors the mobile
// ReportBlockSheet — same backend endpoints, same target types, same reasons.
// Required by App Store guideline 1.2: every piece of UGC must be reportable,
// and every author must be blockable.
//
// Props
//   targetType    — 'post' | 'comment' | 'user' | 'vehicle' | 'review' | 'message' |
//                   'event' | 'event_photo' | 'event_comment' | 'event_announcement' |
//                   'route' | 'route_review' | 'landmark' | 'landmark_review'
//   targetId      — id of the reportable row
//   targetUserId  — author of the content (powers the Block CTA)
//   targetLabel   — short noun shown in titles ("post", "comment", "review")
//   onReported    — called after a report succeeds; receives { targetType, targetId }
//   onBlocked     — called after a block succeeds; receives the blockedUserId
//   triggerSize   — 'small' | 'default'
//   triggerStyle  — extra inline style for the trigger button
//   hideBlock     — pass true to suppress the Block CTA (e.g. on your own content)

const REASONS = [
  { key: 'spam',           label: 'Spam' },
  { key: 'harassment',     label: 'Harassment or bullying' },
  { key: 'hate_speech',    label: 'Hate speech' },
  { key: 'violence',       label: 'Violence or threats' },
  { key: 'sexual_content', label: 'Sexual or nude content' },
  { key: 'self_harm',      label: 'Self-harm' },
  { key: 'illegal',        label: 'Illegal activity' },
  { key: 'misinformation', label: 'False information' },
  { key: 'impersonation',  label: 'Impersonation' },
  { key: 'other',          label: 'Something else' },
];

// Clean stroked icons (Lucide-style) — matches the rest of the codebase's
// inline SVG approach. House rule: no emojis anywhere in UI.
const FlagIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const BanIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);

const ReportBlockMenu = ({
  targetType,
  targetId,
  targetUserId,
  targetLabel = 'content',
  onReported,
  onBlocked,
  triggerSize = 'default',
  triggerStyle,
  hideBlock = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [stage, setStage] = useState(null);        // null | 'reasons' | 'details' | 'blockConfirm'
  const [reason, setReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const wrapperRef = useRef(null);

  const closeAll = () => {
    setMenuOpen(false);
    setStage(null);
    setReason(null);
    setDescription('');
    setSubmitting(false);
  };

  // Click-outside on the dropdown only; the modal stages use the overlay
  // backdrop click to close so they don't get caught by this listener.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const submitReport = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/moderation/reports', {
        targetType,
        targetId,
        reason,
        description: description.trim() || undefined,
      });
      toast.success('Thanks — our team will review this within 24 hours.');
      onReported?.({ targetType, targetId });
      closeAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  const doBlock = async () => {
    if (!targetUserId || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/moderation/blocks', { userId: targetUserId });
      toast.success("You won't see their content anymore.");
      onBlocked?.(targetUserId);
      closeAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not block user.');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerDims = triggerSize === 'small'
    ? { width: 26, height: 26, fontSize: 14 }
    : { width: 32, height: 32, fontSize: 16 };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
        aria-label="More options"
        title="More options"
        style={{
          ...triggerDims,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: 'transparent',
          border: 'none',
          color: '#9aa3b2',
          cursor: 'pointer',
          lineHeight: 1,
          padding: 0,
          ...(triggerStyle || {}),
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        ⋯
      </button>

      {menuOpen && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={glassMenuStyle}
        >
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setStage('reasons'); }}
            style={menuItemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <FlagIcon />
            <span>Report {targetLabel}</span>
          </button>
          {!hideBlock && targetUserId ? (
            <button
              type="button"
              onClick={() => { setMenuOpen(false); setStage('blockConfirm'); }}
              style={{ ...menuItemStyle, color: '#ff8585' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(232,69,69,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <BanIcon />
              <span>Block user</span>
            </button>
          ) : null}
        </div>
      )}

      {stage === 'reasons' && (
        <Modal onClose={closeAll} title={`Why are you reporting this ${targetLabel}?`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {REASONS.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => { setReason(r.key); setStage('details'); }}
                style={listRowStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{r.label}</span>
                <span style={{ color: '#5a657a' }}>›</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {stage === 'details' && (
        <Modal onClose={closeAll} title="Any details?">
          <p style={{ color: '#9aa3b2', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: 14 }}>
            Tell us anything else our team should know. Optional — your report will still be reviewed if you skip this.
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what's wrong (optional)"
            maxLength={2000}
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: '#0f131c', color: '#f0f1f5',
              border: '1px solid #2a3142', borderRadius: 8,
              padding: 12, fontSize: '0.92rem',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={() => setStage('reasons')} style={ghostBtn}>Back</button>
            <button
              type="button"
              onClick={submitReport}
              disabled={submitting}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </button>
          </div>
        </Modal>
      )}

      {stage === 'blockConfirm' && (
        <Modal onClose={closeAll} title="Block this user?">
          <p style={{ color: '#c8cfe0', fontSize: '0.92rem', lineHeight: 1.55, marginBottom: 18 }}>
            They won't be able to see your profile or content, and you won't see theirs. This happens immediately.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={closeAll} style={ghostBtn}>Cancel</button>
            <button
              type="button"
              onClick={doBlock}
              disabled={submitting}
              style={{ ...dangerBtn, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? 'Blocking…' : 'Block'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Internal modal shell ────────────────────────────────────────────────────
// We render our own small modal (rather than reusing .modal CSS) because the
// app's existing .modal has a min-height of 70vh which is way too tall for
// what's essentially a confirm dialog.
const Modal = ({ title, onClose, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      zIndex: 2100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.16) 0%, rgba(220,225,235,0.09) 60%, rgba(200,210,225,0.12) 100%)',
        border: '1px solid rgba(255,255,255,0.26)',
        borderRadius: 16,
        width: '100%', maxWidth: 460,
        boxShadow: '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.35)',
        backdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
        overflow: 'hidden',
      }}
    >
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h3 style={{ margin: 0, color: '#f0f1f5', fontSize: '1.02rem', fontWeight: 700 }}>{title}</h3>
        <button
          type="button" onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent', border: 'none', color: '#9aa3b2',
            cursor: 'pointer', fontSize: '1.4rem', lineHeight: 1, padding: 0,
            width: 28, height: 28, borderRadius: 999,
          }}
        >×</button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);

// Glass dropdown — matches the modal's linear-gradient + backdrop-filter so
// the menu reads as the same material as every other panel in the app.
const glassMenuStyle = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
  minWidth: 200,
  background: 'linear-gradient(145deg, rgba(255,255,255,0.16) 0%, rgba(220,225,235,0.09) 60%, rgba(200,210,225,0.12) 100%)',
  border: '1px solid rgba(255,255,255,0.26)',
  borderRadius: 14,
  boxShadow: '0 16px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
  padding: 6,
  zIndex: 50,
  backdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
  WebkitBackdropFilter: 'blur(28px) saturate(160%) brightness(1.05)',
  overflow: 'hidden',
};

const menuItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
  padding: '10px 12px',
  background: 'transparent', border: 'none',
  color: '#f0f1f5', cursor: 'pointer',
  fontSize: '0.9rem', fontWeight: 500,
  textAlign: 'left', borderRadius: 10,
  transition: 'background 0.15s ease',
};
const listRowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  width: '100%', padding: '12px 14px',
  background: 'transparent', border: 'none',
  color: '#e6e9f0', cursor: 'pointer',
  fontSize: '0.94rem', fontWeight: 500,
  textAlign: 'left', borderRadius: 8,
};
const ghostBtn = {
  padding: '9px 16px', borderRadius: 999,
  background: 'transparent', color: '#c8cfe0',
  border: '1px solid #3a445c', cursor: 'pointer',
  fontSize: '0.86rem', fontWeight: 600,
};
const primaryBtn = {
  padding: '9px 18px', borderRadius: 999,
  background: '#ededef', color: '#0a0a0a',
  border: 'none', cursor: 'pointer',
  fontSize: '0.88rem', fontWeight: 700,
};
const dangerBtn = {
  padding: '9px 18px', borderRadius: 999,
  background: '#e84545', color: '#fff',
  border: 'none', cursor: 'pointer',
  fontSize: '0.88rem', fontWeight: 700,
};

export default ReportBlockMenu;
